import AppBarTools from '@/components/system/Layout/LayoutWebCore/WebCoreAppBar/ToolBar';
import TelegramMiniAppAutoLogin from '@/components/system/Auth/TelegramMiniAppAutoLogin';
import LocalizedLink from '@/components/ui/LocalizedLink';
import MoonLogo from '@/components/biz/MoonLogo';
import { isTelegramMiniAppView } from '@/utils/checkRuntime';
import RewardsDropdown from '../SideBarHeader/rewards';
import AuthModal from '@/components/system/Auth/AuthModal';
import dynamic from 'next/dynamic';
import React from 'react';
import { useRouter } from 'next/router';
import LangSimpleSwitch from '@/components/system/LangSimpleSwitch';
import {
  APP_SIDE_BAR_COLLAPSE_WIDTH,
  APP_SIDE_BAR_EXPAND_WIDTH,
  APP_FIXED_HEADER_HEIGHT,
} from '@/constants/global';
import { Box } from '@mui/material';
import { useAppDrawer } from './hooks/useAppDrawer';

// 奖励区域组件 - 使用 TailwindCSS
export const RewardsSection = ({
  appSidebarOpen,
}: {
  appSidebarOpen?: boolean;
}) => {
  return (
    <div className="h-[42px] inline-flex justify-center items-center">
      <RewardsDropdown />
    </div>
  );
};

// 导航栏组件 - 使用 TailwindCSS
const NavBar: React.FC = () => {
  const navItems = [
    { label: 'Home', href: '/wow' },
    { label: 'Fomo Power', href: '/games/fomo/zones' },
  ];

  return (
    <nav className="flex items-center ml-6">
      {navItems.map((item) => (
        <LocalizedLink
          key={item.href}
          href={item.href}
          className="px-2 py-2 text-sm font-medium text-white hover:text-white transition-colors duration-200"
        >
          {item.label}
        </LocalizedLink>
      ))}
    </nav>
  );
};

const WalletWidget = dynamic(
  () => import('@/components/features/wallet/WalletWidget'),
  {
    ssr: false,
  },
);

const WalletWidgetPlaceholder = dynamic(
  () => import('@/components/features/wallet/WalletWidgetPlaceholder'),
  {
    ssr: false,
  },
);

const renderWalletComponent = (
  isTemporaryLogin: boolean,
  loggedIn: boolean,
) => {
  if (isTemporaryLogin) {
    return <WalletWidgetPlaceholder />;
  }
  if (loggedIn) {
    return <WalletWidget />;
  }
  return null;
};

interface NewAppBarContentProps {
  isMobile: boolean;
  loggedIn: boolean;
  isTemporaryLogin: boolean;
}

export const NewAppBarContent: React.FC<NewAppBarContentProps> = ({
  isMobile,
  loggedIn,
  isTemporaryLogin,
}) => {
  const router = useRouter();
  const isAccountPage = router.pathname.startsWith('/account');
  const shouldHideAuthModal = !loggedIn && isAccountPage;
  const { appSidebarOpen, toggleDrawerOpen, handleHeaderIconClick } =
    useAppDrawer(isMobile);

  // 检查是否需要隐藏侧边导航栏的页面
  const isWoWHomePage = router.pathname === '/wow';
  const isAuthPage =
    router.pathname === '/account/login' ||
    router.pathname === '/account/register';
  const shouldHideSidebar = isMobile || isWoWHomePage || isAuthPage;

  // 当侧边栏被隐藏时，应该使用收起状态的 marginLeft
  const effectiveAppSidebarOpen = shouldHideSidebar ? false : appSidebarOpen;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        ml: isMobile
          ? 1
          : `${effectiveAppSidebarOpen ? APP_SIDE_BAR_EXPAND_WIDTH : 0}px`,
        justifyContent: 'flex-end',
        height: `${APP_FIXED_HEADER_HEIGHT}px`,
        backgroundColor: 'bg.main',
      }}
    >
      {isMobile ? (
        // 移动端使用两列布局：左侧Logo，右侧钱包 - 使用 TailwindCSS
        <>
          <div className="flex items-center flex-none justify-start">
            <MoonLogo variant="header" />
          </div>
          <div className="flex items-center flex-1 justify-end">
            {!isWoWHomePage && (
              <div className="mr-2">
                {loggedIn && renderWalletComponent(isTemporaryLogin, loggedIn)}
              </div>
            )}
            {!shouldHideAuthModal && <AuthModal />}
          </div>
        </>
      ) : (
        // 桌面端保持三列布局 - 使用 TailwindCSS
        <>
          {/* <div className="flex items-center flex-1 justify-start">
            <MoonLogo variant="header" />
          </div> */}
          {/* <Box
            sx={{
              ml: `${appSidebarOpen ? APP_SIDE_BAR_EXPAND_WIDTH : APP_SIDE_BAR_COLLAPSE_WIDTH}px`,
            }}
          ></Box> */}
          {!isWoWHomePage && (
            <div className="flex items-center justify-center flex-1">
              {renderWalletComponent(isTemporaryLogin, loggedIn)}
            </div>
          )}
          <div className="absolute right-8">
            <div className="flex justify-between items-center gap-2.5">
              <div className="flex items-center h-[38px] w-fit px-2 cursor-pointer border-2 border-white/15 pr-0 rounded-xl">
                <LangSimpleSwitch iconSize="20px" />
              </div>
              {isTelegramMiniAppView ? (
                <TelegramMiniAppAutoLogin />
              ) : (
                !shouldHideAuthModal && <AuthModal />
              )}
            </div>
          </div>
        </>
      )}
    </Box>
  );
};

// 导出默认组件，保持向后兼容
export default NewAppBarContent;
